import React from 'react';
import { Container, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

const PrivacyPage = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Paper sx={{ p: { xs: 3, md: 6 } }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          Privacy Notice
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          Effective: 3 November 2025 (Sections 8A and 8B added October 2026, updated November 2026)
        </Typography>

        <Typography paragraph>
          IvyAstrology.com is owned and operated by C & E Technology Ltd, 28 Croftwell Square, Rathcoole, Co. Dublin, D24 F6PN, Ireland ("we", "us", "our"). We respect your privacy and are committed to protecting your personal information.
        </Typography>

        <Typography paragraph>
          This Privacy Notice explains how we collect, use, share and secure your data when you use IvyAstrology.com and related touch‑points, including when you:
        </Typography>

        <Box component="ul" sx={{ pl: 4, mb: 3 }}>
          <li>browse or purchase through IvyAstrology.com (the "Website"),</li>
          <li>create an account, manage a subscription, or complete checkout,</li>
          <li>receive horoscope emails or WhatsApp messages from us, or</li>
          <li>communicate with us by email, WhatsApp, contact forms or social channels.</li>
        </Box>

        <Typography paragraph>
          We refer to these touch‑points collectively as the "IvyAstrology Services".
        </Typography>

        {/* Section 1 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            1. Who is the data controller?
          </Typography>
          <Typography paragraph>
            C & E Technology Ltd is the data controller for all personal information collected via IvyAstrology.com.
          </Typography>
        </Box>

        {/* Section 2 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            2. What personal information do we collect?
          </Typography>
          <Typography paragraph>
            We collect information that you provide directly to us, information generated by your use of the IvyAstrology Services, and information from certain third parties (e.g., payments, messaging, analytics).
          </Typography>

          <TableContainer sx={{ my: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Examples</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>How we collect</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Information you give us</TableCell>
                  <TableCell>Name, email address, country/time zone; questions for your reading; account details; subscription choices; marketing preferences.</TableCell>
                  <TableCell>Checkout, account area, contact form, email/WhatsApp.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Birth & astrology inputs</TableCell>
                  <TableCell>Birth date, exact time, and birth location (to calculate your natal chart); optional relationship details for compatibility reports.</TableCell>
                  <TableCell>Checkout, account form, questionnaires.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Generated astrology data</TableCell>
                  <TableCell>Natal chart calculations; transits; compatibility outputs; predictions; report PDFs; interpretation notes attached to your profile.</TableCell>
                  <TableCell>Generated by us from your inputs.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Payment & billing</TableCell>
                  <TableCell>Last 4 digits/card brand; Payment Method/Intent ID/token; billing address; subscription status/renewal date. We never see full card numbers.</TableCell>
                  <TableCell>Entered by you in secure checkout (e.g., Stripe) and returned to us as tokens/metadata.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Communications</TableCell>
                  <TableCell>Emails and WhatsApp messages with our support; metadata such as date/time and your phone number for WhatsApp.</TableCell>
                  <TableCell>Email/WhatsApp/social DMs.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Automatic device/usage data</TableCell>
                  <TableCell>IP address, device type, browser, pages viewed, referring URL, time on site, session identifiers, cookies, approximate location from IP, language, event logs.</TableCell>
                  <TableCell>Cookies, server logs, analytics scripts.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Third‑party signals</TableCell>
                  <TableCell>Fraud/risk scores from payment processor; geocoding for birth place/time zone; deliverability/engagement signals from email or WhatsApp providers.</TableCell>
                  <TableCell>Secure API calls to our vendors.</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Please don't share sensitive information (e.g., health, political opinions). If you voluntarily include such special category data in a free‑text message, we will process it only with your explicit consent and solely to respond to your query; you can withdraw that consent at any time (see Section 9).
          </Typography>
        </Box>

        {/* Section 3 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            3. Why do we process your data?
          </Typography>

          <TableContainer sx={{ my: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Examples</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Legal basis (GDPR)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Provide the Services</TableCell>
                  <TableCell>Create your account; calculate natal charts and compatibility; generate and deliver readings/reports; manage subscriptions and renewals; send service emails (order/renewal confirmations).</TableCell>
                  <TableCell>Contract performance (Art. 6(1)(b))</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Customer support</TableCell>
                  <TableCell>Answer questions; reschedule/cancel; troubleshoot access; handle complaints.</TableCell>
                  <TableCell>Legitimate interests (service quality) (Art. 6(1)(f))</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Payments & fraud prevention</TableCell>
                  <TableCell>Process charges, refunds where applicable; detect misuse/chargeback risk.</TableCell>
                  <TableCell>Contract performance; Legitimate interests; Legal obligation (tax) (Art. 6(1)(b),(f),(c))</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Personalisation & improvement</TableCell>
                  <TableCell>Tailor content frequency; improve interpretations and UX; debug and analytics.</TableCell>
                  <TableCell>Legitimate interests (product improvement) (Art. 6(1)(f))</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Marketing (optional)</TableCell>
                  <TableCell>Newsletters, promotions, "today's horoscope" emails/WhatsApps.</TableCell>
                  <TableCell>Consent (opt‑in) (Art. 6(1)(a)); or "soft opt‑in" for existing customers for similar services where permitted, with opt‑out in every message (ePrivacy rules).</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Compliance</TableCell>
                  <TableCell>Invoices, accounting and tax records; legal claims and regulatory requests.</TableCell>
                  <TableCell>Legal obligation; Legitimate interests (establish/defend claims) (Art. 6(1)(c),(f))</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Special category data (if you share it)</TableCell>
                  <TableCell>You include health/sexual orientation/religion in a message.</TableCell>
                  <TableCell>Explicit consent (Art. 9(2)(a))—you can withdraw any time.</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            <strong>Automated decisions/profiling:</strong> We use automated calculations (e.g., ephemeris math) to produce astrology outputs based on your birth inputs. We do not make decisions producing legal or similarly significant effects based solely on automated processing (Art. 22 GDPR).
          </Typography>
        </Box>

        {/* Section 4 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            4. Cookies & similar technologies
          </Typography>
          <Typography paragraph>We use:</Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li><strong>Essential cookies</strong> – keep you logged in, remember cart/subscription steps, maintain security.</li>
            <li><strong>Analytics (privacy‑friendly)</strong> – e.g., Plausible.io or equivalent; no cross‑site tracking.</li>
            <li><strong>Checkout cookies</strong> – e.g., Stripe to enable secure payment methods (Apple Pay/Google Pay), prevent fraud, and remember your checkout state.</li>
            <li><strong>Marketing (optional)</strong> – if enabled/accepted (e.g., Google/Meta pixels), used only in accordance with your consent/choices.</li>
          </Box>
          <Typography paragraph>
            You can disable non‑essential cookies via our banner or your browser settings; the Website will still function, but some features may be limited.
          </Typography>
        </Box>

        {/* Section 5 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            5. Who do we share data with?
          </Typography>
          <Typography paragraph>
            We never sell or rent your personal data. We share it only with trusted service providers under data‑processing agreements, and only as necessary:
          </Typography>

          <TableContainer sx={{ my: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Safeguard</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Stripe Payments Europe, Ltd. (and/or another PCI‑compliant processor)</TableCell>
                  <TableCell>Process payments, store reusable payment token for renewals, detect fraud.</TableCell>
                  <TableCell>PCI‑DSS compliant; EU entity where applicable; SCCs/adequacy if data goes outside EEA.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Amazon Web Services (EU‑West‑1)</TableCell>
                  <TableCell>Host website, databases, files, backups.</TableCell>
                  <TableCell>AWS DPA; encryption at rest/in transit.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Email provider (e.g., SendGrid/Twilio or equivalent)</TableCell>
                  <TableCell>Send order confirmations, subscription/receipt emails, and (if opted in) marketing emails.</TableCell>
                  <TableCell>SCCs/adequacy; contractual DP terms.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>WhatsApp (WhatsApp Ireland Ltd / WhatsApp LLC) and/or messaging gateway (e.g., Twilio)</TableCell>
                  <TableCell>Deliver service or marketing messages if you opt to contact us or opt‑in for WhatsApp updates.</TableCell>
                  <TableCell>WhatsApp/Twilio DP terms; SCCs/adequacy where relevant.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Analytics provider (e.g., Plausible.io or equivalent)</TableCell>
                  <TableCell>Privacy‑friendly analytics and performance monitoring.</TableCell>
                  <TableCell>EU hosting where available; SCCs/adequacy if transferred.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Geocoding/time‑zone/ephemeris tools (where used)</TableCell>
                  <TableCell>Convert birth place to coordinates/time zone; astronomical calculations.</TableCell>
                  <TableCell>Data minimised; SCCs/adequacy where relevant.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Professional advisers (accountants, legal)</TableCell>
                  <TableCell>Audits and statutory filings; legal claims.</TableCell>
                  <TableCell>Confidentiality and DP obligations.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Authorities</TableCell>
                  <TableCell>Where required by law or to protect rights/safety.</TableCell>
                  <TableCell>Legal obligation.</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography paragraph>
            We may share aggregated or de‑identified insights (e.g., total subscribers per month), which cannot reasonably identify you.
          </Typography>
        </Box>

        {/* Section 6 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            6. International transfers
          </Typography>
          <Typography paragraph>
            If we transfer personal data outside the EEA/UK, we use a lawful transfer mechanism such as:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>European Commission Standard Contractual Clauses (SCCs),</li>
            <li>an adequacy decision (e.g., EU–US Data Privacy Framework, where applicable), or</li>
            <li>other safeguards permitted by GDPR.</li>
          </Box>
          <Typography paragraph>
            You can request a copy of the relevant transfer safeguards (with redactions where necessary) via the contact details in Section 13.
          </Typography>
        </Box>

        {/* Section 7 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            7. Keeping your data secure
          </Typography>
          <Typography paragraph>
            We apply appropriate technical and organisational measures, including:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>TLS encryption in transit (HTTPS everywhere).</li>
            <li>Encrypted storage (e.g., AWS encryption at rest, AES‑256).</li>
            <li>Payment processors handle card data; our systems store only payment tokens/IDs.</li>
            <li>Role‑based access controls; strong passwords; admin access logging; recommended multi‑factor authentication.</li>
            <li>Daily encrypted backups retained 30 days then auto‑deleted.</li>
            <li>Vendor due‑diligence and data‑processing agreements.</li>
            <li>Staff/contractor confidentiality commitments.</li>
          </Box>
          <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No system is 100% secure. Please use unique, strong passwords and contact us immediately if you suspect unauthorised use of your account.
          </Typography>
        </Box>

        {/* Section 8 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            8. How long do we keep your information?
          </Typography>
          <Typography paragraph>
            We keep data only as long as necessary for the purposes set out above, and to meet legal/accounting obligations.
          </Typography>

          <TableContainer sx={{ my: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Typical retention</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Account profile & birth/astrology inputs</TableCell>
                  <TableCell>While your account is active and 24 months after last activity; deleted earlier on verified request (see backups note below).</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Generated reports/readings</TableCell>
                  <TableCell>While account active and 24 months after last activity (to let you re‑download), then deleted or anonymised.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Subscription, order & invoice records</TableCell>
                  <TableCell>6 years (Irish tax law/Revenue record‑keeping).</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Support emails/WhatsApp logs</TableCell>
                  <TableCell>24 months from last contact unless required longer for disputes.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Marketing consent & preferences</TableCell>
                  <TableCell>Until you withdraw consent; minimal logs retained 24 months to evidence compliance.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Cookies/analytics events</TableCell>
                  <TableCell>30 days (analytics) and session‑only (essential), unless otherwise stated in the banner.</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            <strong>Backups:</strong> Deleted items may persist in encrypted backups for up to 30 days before being overwritten (35 days for the Personal Venus Star Point Reading, see Section 8A).
          </Typography>
        </Box>

        {/* Section 8A: Personal Venus Star Point Reading */}
        <Box sx={{ mt: 4, mb: 3 }} id="venus-star-point-reading">
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            8A. Personal Venus Star Point Reading
          </Typography>
          <Typography paragraph>
            When you order a Personal Venus Star Point Reading, this section applies in addition to the rest of this notice.
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>
              <strong>What we collect:</strong> the name to print on the reading, your email address, and the birth details you
              enter (birth date, birth time or that it is unknown, birthplace with its coordinates, and the timezone or UTC
              offset used). If you order for someone else, you confirm that you have their permission to use their birth data;
              the reading is emailed to you. The person a reading is about must be 16 or older on the order date.
            </li>
            <li>
              <strong>Why:</strong> to calculate the chart and to prepare and deliver the reading you bought (performance of a
              contract), and to keep the records the law requires for sales (legal obligation).
            </li>
            <li>
              <strong>AI assistance:</strong> the reading is calculated by our software and written with AI assistance. The AI
              provider is Anthropic (the Claude models). For each section, Anthropic receives chart positions, dates and houses.
              It never receives your name or email address. Nothing we send states your birth date, birth time or birthplace,
              but those positions and dates are precise enough that anyone holding them and a free ephemeris (a table of where
              the planets were on any date) could work out the day you were born and, if you gave us a birth time, that time
              closely and your birthplace roughly.
            </li>
            <li>
              <strong>Where it is stored:</strong> on Amazon Web Services in the EU (Ireland, eu-west-1), encrypted, in storage
              that only the reading system can read.
            </li>
            <li>
              <strong>Payment:</strong> Stripe processes the payment for C & E Technology Ltd. We receive no card details; we keep
              the payment reference and the country code of your billing address (for example "IE"), which we need for VAT
              (EU One-Stop-Shop) reporting. We do not keep your billing address.
            </li>
            <li>
              <strong>Birthplace search:</strong> the place suggestions in the order form come from OpenStreetMap Nominatim,
              queried directly from your browser, so Nominatim sees what you type in that field and your IP address.
            </li>
            <li>
              <strong>Email copies:</strong> a copy of the email that delivers your reading, and the draft Claudia reviews during
              launch, are kept in our mailbox and deleted on the same schedule as the reading.
            </li>
            <li>
              <strong>How long:</strong> your birth details, the reading and the order record are deleted automatically two
              years after your order. Deleted records can remain in encrypted backups for up to 35 days. A free re-run with a
              birth time you find later does not extend this.
            </li>
            <li>
              <strong>Earlier deletion:</strong> write to <strong>privacy@ivyastrology.com</strong> or reply to your reading's
              email, and we will delete your order data and our email copies (we keep only what the law requires for the
              sale itself, such as the invoice record).
            </li>
          </Box>
        </Box>

        {/* Section 8B: Compatibility check and Full Compatibility Report */}
        <Box sx={{ mt: 4, mb: 3 }} id="compatibility-report">
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            8B. Compatibility check and Full Compatibility Report
          </Typography>
          <Typography paragraph>
            When you use the compatibility check or order a Full Compatibility Report, this section applies in addition to the
            rest of this notice.
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>
              <strong>What we collect:</strong> the two first names, the birth details you enter for both people (birth date,
              birth time if you give it, birthplace with its coordinates), and, for the full report, the email address you give
              at checkout and, if you are logged in, your account. By ordering, you confirm that you have the other person's
              permission to use their birth details.
            </li>
            <li>
              <strong>Why:</strong> to calculate both charts and to prepare and deliver the check or the report you bought
              (performance of a contract with you), to keep the records the law requires for sales (legal obligation), and,
              for the other person's details, our legitimate interest in providing the report you asked for, relying on your
              confirmation that you have their permission.
            </li>
            <li>
              <strong>AI assistance:</strong> the charts are calculated by our software, and the free check's summary and the
              full report are written with AI assistance. The AI provider is Anthropic (the Claude models). Anthropic receives the
              two first names, the chart placements and, for the full report, the dates of the coming year's events. It never
              receives your email address. Nothing we send states a birth date, birth time or birthplace, but those placements
              and dates sit beside the first names and are precise enough that anyone holding them and a free ephemeris (a table
              of where the planets were on any date) could work out a good deal. From a full report's requests: the day each
              person was born, to within a few days and often exactly, and usually, where a birth time was given, that time to
              within a few hours and some indication of the birthplace. From the free check's requests: a short list of possible
              days for each person's birth.
            </li>
            <li>
              <strong>Where it is stored:</strong> on Amazon Web Services in the EU (Ireland, eu-west-1).
            </li>
            <li>
              <strong>Payment:</strong> Stripe processes the payment for C & E Technology Ltd and receives the two first names as
              the order description. We receive no card details.
            </li>
            <li>
              <strong>Birthplace search:</strong> the place suggestions come from OpenStreetMap Nominatim, queried directly from
              your browser, so Nominatim sees what you type in that field and your IP address.
            </li>
            <li>
              <strong>How long:</strong> the report and the birth details it was written from are kept so that its link keeps
              working; if you were logged in, it is also listed in My Stars. Write to <strong>privacy@ivyastrology.com</strong> at
              any time and we will delete them (we keep only what the law requires for the sale itself, such as the invoice record).
            </li>
          </Box>
        </Box>

        {/* Section 9 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            9. Your rights
          </Typography>
          <Typography paragraph>
            Under GDPR/UK GDPR, you can:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li><strong>Access</strong> the personal data we hold about you.</li>
            <li><strong>Rectify</strong> inaccurate or incomplete data.</li>
            <li><strong>Erase</strong> your data in certain circumstances ("right to be forgotten").</li>
            <li><strong>Restrict or object</strong> to processing (including a right to object to direct marketing at any time).</li>
            <li><strong>Portability</strong> – receive your data in a structured, commonly used, machine‑readable format.</li>
            <li><strong>Withdraw consent</strong> at any time where we rely on consent (e.g., marketing or any special‑category data you chose to share).</li>
            <li><strong>Lodge a complaint</strong> with a supervisory authority (see Section 13).</li>
          </Box>
          <Typography paragraph>
            To exercise your rights, email <strong>privacy@ivyastrology.com</strong>. We will respond within 30 days (or explain if more time is needed for complex requests). We may ask for reasonable identity verification.
          </Typography>
          <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            <strong>Consequences of deletion:</strong> Removing your birth data or reports may limit or end our ability to provide personalised Services or subscription content.
          </Typography>
        </Box>

        {/* Section 10 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            10. Marketing & advertising
          </Typography>
          <Typography paragraph>
            We send marketing (e.g., promotions, newsletters, daily/weekly horoscopes) only with your consent, or under a permitted "soft opt‑in" to existing customers for similar Services (with an unsubscribe link in every message).
          </Typography>
          <Typography paragraph>
            You can unsubscribe at any time via the link in our emails, by replying STOP on WhatsApp, or by emailing <strong>privacy@ivyastrology.com</strong>.
          </Typography>
          <Typography paragraph>
            We may run privacy‑respecting campaigns on platforms such as Google or Meta. Where we use conversion tracking or custom audiences, we do so only in line with your consent/choices and applicable law. We do not upload identifiable customer lists for targeting without your explicit permission.
          </Typography>
        </Box>

        {/* Section 11 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            11. Children
          </Typography>
          <Typography paragraph>
            IvyAstrology.com is for users 18+. We do not knowingly collect data from children. If you believe a minor has provided us data, contact <strong>privacy@ivyastrology.com</strong> and we will delete it.
          </Typography>
        </Box>

        {/* Section 12 */}
        <Box sx={{ mt: 4, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            12. Updates to this notice
          </Typography>
          <Typography paragraph>
            We may update this Privacy Notice as our Services or legal requirements evolve. Material changes will be highlighted on the Website or notified by email (where appropriate). Your continued use after the effective date signifies acceptance of the updated notice.
          </Typography>
        </Box>

        {/* Section 13 */}
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            13. Contact & complaints
          </Typography>
          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2 }}>
            <Typography paragraph sx={{ mb: 1 }}>
              <strong>Data Protection Contact</strong>
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              <strong>C & E Technology Ltd</strong> (trading as IvyAstrology.com)
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              28 Croftwell Square, Rathcoole, Co. Dublin, D24 F6PN, Ireland
            </Typography>
            <Typography paragraph sx={{ mb: 1 }}>
              Email: <a href="mailto:privacy@ivyastrology.com">privacy@ivyastrology.com</a> (privacy requests) / <a href="mailto:contact@ivyastrology.com">contact@ivyastrology.com</a> (general)
            </Typography>
          </Box>
          <Typography paragraph sx={{ mt: 2 }}>
            If you feel we haven't handled your data correctly, you can complain to the Irish Data Protection Commission (www.dataprotection.ie) or your local authority. We would appreciate the chance to resolve your concerns first—please contact us.
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center">
          © 2025 C & E Technology Ltd. All rights reserved.
        </Typography>
      </Paper>
    </Container>
  );
};

export default PrivacyPage;
